/** Metric names and dimensions must be static; never include app IDs, code, secrets, or error messages. */
export interface CodeSandboxMetricAttributes {
    readonly provider: "daytona" | "modal";
    readonly startup_kind: "created" | "reused" | "unknown";
    readonly result:
        | "success"
        | "invalid_input"
        | "user_code_error"
        | "output_error"
        | "provider_error"
        | "timeout"
        | "internal_error";
    readonly stage: "none" | "validation" | "startup" | "upload" | "execution" | "output" | "unknown";
}

export interface PluginCounterAttributes {
    readonly "code_sandbox.executions": CodeSandboxMetricAttributes;
    readonly "code_sandbox.rejections": {
        readonly provider: "daytona" | "modal";
        readonly reason: "validation" | "configuration";
    };
    readonly "code_sandbox.cleanup_errors": {
        readonly provider: "daytona" | "modal";
        readonly operation: "stop" | "delete" | "terminate" | "detach";
    };
}

export interface ModalOperationMetricAttributes {
    readonly operation: "exec_start" | "wait" | "stdout_read" | "stderr_read";
    readonly status: "success" | "error";
}

export interface ModalExecutionDiagnostics {
    readonly durations: Partial<Record<ModalOperationMetricAttributes["operation"], number>>;
    failedOperation?: ModalOperationMetricAttributes["operation"];
    error?: ModalErrorFingerprint;
    estimatedRemainingMsAtFailure?: number;
    sdkVersion: string;
    readonly config: {
        readonly provenance: "configured_creation";
        readonly commandTimeoutMs: number;
        readonly timeoutMs: number;
        readonly idleTimeoutMs: number;
        readonly memoryMiB: number;
        readonly memoryLimitMiB: number;
        readonly nodeMaxOldSpaceSizeMiB: number;
    };
}

export interface ModalErrorFingerprint {
    readonly name: string;
    readonly grpcCode?: number;
    readonly cause?: Omit<ModalErrorFingerprint, "cause">;
}

export interface PluginDurationAttributes {
    readonly "code_sandbox.modal.operation.duration": ModalOperationMetricAttributes;
    readonly "code_sandbox.duration": CodeSandboxMetricAttributes;
    readonly "code_sandbox.startup.duration": CodeSandboxMetricAttributes;
    readonly "code_sandbox.upload.duration": CodeSandboxMetricAttributes;
    readonly "code_sandbox.execution.duration": CodeSandboxMetricAttributes;
    readonly "code_sandbox.cleanup.duration": CodeSandboxMetricAttributes;
}

/** Runtime metrics, independent of sampled traces. Implementations must not throw. */
export interface PluginMetrics {
    readonly recordCodeSandboxModalFailure?: (
        attributes: CodeSandboxMetricAttributes,
        diagnostics: ModalExecutionDiagnostics
    ) => void;
    readonly incrementCounter: <Name extends keyof PluginCounterAttributes>(
        name: Name,
        attributes: PluginCounterAttributes[Name]
    ) => void;
    /** Record host-observed elapsed milliseconds. */
    readonly recordDuration: <Name extends keyof PluginDurationAttributes>(
        name: Name,
        milliseconds: number,
        attributes: PluginDurationAttributes[Name]
    ) => void;
}

export const noOpPluginMetrics: PluginMetrics = {
    incrementCounter: () => undefined,
    recordDuration: () => undefined,
};
